import { useEffect, useState, useMemo, useCallback } from 'react';
import {
    Modal, ModalDialog, ModalClose,
    Box, Stack, Typography, Chip,
    Input, Button, IconButton, Textarea,
} from '@mui/joy';

import CreditCardRoundedIcon         from '@mui/icons-material/CreditCardRounded';
import PaymentsRoundedIcon           from '@mui/icons-material/PaymentsRounded';
import PointOfSaleRoundedIcon        from '@mui/icons-material/PointOfSaleRounded';
import AddRoundedIcon                from '@mui/icons-material/AddRounded';
import RemoveRoundedIcon             from '@mui/icons-material/RemoveRounded';
import ReceiptLongRoundedIcon        from '@mui/icons-material/ReceiptLongRounded';
import DeleteOutlineRoundedIcon      from '@mui/icons-material/DeleteOutlineRounded';
import Inventory2RoundedIcon         from '@mui/icons-material/Inventory2Rounded';
import LocalOfferRoundedIcon         from '@mui/icons-material/LocalOfferRounded';
import PhoneIphoneRoundedIcon        from '@mui/icons-material/PhoneIphoneRounded';
import CategoryRoundedIcon           from '@mui/icons-material/CategoryRounded';
import ScheduleRoundedIcon           from '@mui/icons-material/ScheduleRounded';
import ShoppingCartRoundedIcon       from '@mui/icons-material/ShoppingCartRounded';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';

import DiscountPickerModal from './DiscountPickerModal';

const MONEY_TYPES = [
    { v: 'transfer', label: 'Перевод',  icon: <CreditCardRoundedIcon  sx={{ fontSize: 16 }} />, c: '#818cf8' },
    { v: 'cash',     label: 'Наличные', icon: <PaymentsRoundedIcon    sx={{ fontSize: 16 }} />, c: '#22c55e' },
    { v: 'terminal', label: 'Терминал', icon: <PointOfSaleRoundedIcon sx={{ fontSize: 16 }} />, c: '#f59e0b' },
];

const MONEY_META = {
    transfer: MONEY_TYPES[0],
    cash:     MONEY_TYPES[1],
    terminal: MONEY_TYPES[2],
};

const inputSx = {
    bgcolor: 'var(--joy-palette-background-level2)',
    border: '1px solid var(--joy-palette-divider)',
    color: 'var(--joy-palette-text-primary)',
    fontSize: 13,
    '&:hover, &:focus-within': {
        borderColor: 'var(--joy-palette-primary-500)',
        bgcolor: 'var(--joy-palette-background-level2)',
    },
};

const sectionLabelSx = {
    fontSize: 10, fontWeight: 700,
    color: 'var(--joy-palette-text-tertiary)',
    textTransform: 'uppercase', letterSpacing: '0.05em',
    mb: 0.4,
};

// «Сейчас» в формате datetime-local
const nowLocal = () => {
    const d = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const calcDiscountAmount = (base, disc) => {
    if (!disc) return 0;
    const v = parseFloat(disc.value) || 0;
    return disc.is_percent ? base * v / 100 : v;
};

// Преобразует ISO-строку (с TZ) в значение для <input type="datetime-local">
// в локальной таймзоне браузера: 'YYYY-MM-DDTHH:MM'.
const isoToDatetimeLocal = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};


/**
 * Модалка оформления реального чека по фантом-чеку (SaleNotification).
 *
 * Контракт onSubmit({ lines, comment?, receipt_datetime? }) — тот же,
 * что у create_order, только без photographer_id/location_id/payment_number_id
 * (бэк берёт их из notification).
 *
 * Props:
 *   open: boolean
 *   notification: SaleNotification (items, comment, photographer_name, payment_number_*)
 *   products:    Product[]
 *   discounts:   Discount[]
 *   categories:  ProductCategory[]   ← новое, нужно для категорийных чипов слева
 *   loading:     boolean
 *   onClose:     () => void
 *   onSubmit:    (payload) => void
 */
export default function ProcessSaleModal({
    open, notification,
    products = [], discounts = [], categories = [],
    loading, onClose, onSubmit,
}) {
    const [items,         setItems]         = useState([]); // [{ _uid, product_id, product, quantity, unit_price, money_type, comment, discount }]
    const [orderDiscount, setOrderDiscount] = useState(null);
    const [comment,       setComment]       = useState('');
    const [receiptDt,     setReceiptDt]     = useState(() => isoToDatetimeLocal(notification?.date_create));

    const [categoryFilter, setCategoryFilter] = useState('all');
    const [search,         setSearch]         = useState('');

    const [discPicker, setDiscPicker] = useState({ open: false, target: 'item', itemIdx: null });

    // Сброс при закрытии
    useEffect(() => {
        if (!open) {
            setItems([]);
            setOrderDiscount(null);
            setComment('');
            setReceiptDt('');
            setCategoryFilter('all');
            setSearch('');
            setDiscPicker({ open: false, target: 'item', itemIdx: null });
        }
    }, [open]);

    // При открытии (или смене notification) — подставляем момент добавления
    // фотографом как значение по умолчанию. Админ может затем поправить вручную.
    useEffect(() => {
        if (open && notification?.date_create) {
            setReceiptDt(isoToDatetimeLocal(notification.date_create));
        }
    }, [open, notification?.date_create]);

    // ── Помощники: позиции ────────────────────────────────────
    const addItemByProduct = useCallback((product) => {
        setItems(prev => {
            // Дедуп: если уже есть «свежая» позиция этого товара ещё
            // без выбранного типа оплаты, скидки и комментария — +1 к qty.
            const existsIdx = prev.findIndex(it =>
                it.product_id === product.id
                && !it.money_type
                && !it.discount && !it.comment,
            );
            if (existsIdx !== -1) {
                return prev.map((it, i) =>
                    i === existsIdx ? { ...it, quantity: it.quantity + 1 } : it,
                );
            }
            return [...prev, {
                _uid: `${product.id}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
                product_id: product.id,
                product,
                quantity: 1,
                unit_price: parseFloat(product.price) || 0,
                money_type: null,   // пользователь обязан выбрать тип оплаты вручную
                comment: '',
                discount: null,
            }];
        });
    }, []);

    const removeItem    = (idx) => setItems(prev => prev.filter((_, i) => i !== idx));
    const updateItemQty = (idx, delta) => setItems(prev => prev.map((it, i) => {
        if (i !== idx) return it;
        const next = it.quantity + delta;
        return next > 0 ? { ...it, quantity: next } : it;
    }));
    const updateItemPrice = (idx, value) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, unit_price: Math.max(0, parseFloat(value) || 0) } : it,
    ));
    const setItemDiscount = (idx, disc) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, discount: disc } : it,
    ));
    const setItemMoneyType = (idx, mt) => setItems(prev => prev.map((it, i) =>
        i === idx ? { ...it, money_type: mt } : it,
    ));

    // ── Подсчёт ───────────────────────────────────────────────
    const itemSubtotal = (it) => it.unit_price * it.quantity;
    const itemTotal    = (it) => Math.max(0, itemSubtotal(it) - calcDiscountAmount(itemSubtotal(it), it.discount));
    const subtotalBeforeOrderDisc = useMemo(
        () => items.reduce((s, it) => s + itemTotal(it), 0),
        [items],
    );
    const orderDiscAmount = useMemo(
        () => calcDiscountAmount(subtotalBeforeOrderDisc, orderDiscount),
        [subtotalBeforeOrderDisc, orderDiscount],
    );
    const grandTotal = Math.max(0, subtotalBeforeOrderDisc - orderDiscAmount);

    // Разбивка subtotal'а по типам оплаты — для отображения в итогах.
    // Позиции без выбранного типа оплаты в разбивку не идут.
    const moneyTypesBreakdown = useMemo(() => {
        const map = new Map();
        for (const it of items) {
            if (!it.money_type) continue;
            map.set(it.money_type, (map.get(it.money_type) || 0) + itemTotal(it));
        }
        return [...map.entries()].map(([mt, total]) => ({ mt, total }));
    }, [items]);

    // Сумма фантома — для сравнения
    const phantomTotal = useMemo(
        () => (notification?.items || []).reduce((s, it) => s + (parseFloat(it.value) || 0), 0),
        [notification],
    );

    // ── Валидация ─────────────────────────────────────────────
    const validation = useMemo(() => {
        if (items.length === 0) return { ok: false, reason: 'Добавьте хотя бы одну позицию' };
        // У каждой позиции должен быть выбран тип оплаты
        if (items.some(it => !it.money_type)) {
            return { ok: false, reason: 'У каждой позиции выберите тип оплаты' };
        }
        // Перевод требует, чтобы у фантом-чека был привязан номер
        const hasTransfer = items.some(it => it.money_type === 'transfer');
        if (hasTransfer && !notification?.payment_number) {
            return { ok: false, reason: 'Есть позиция-перевод, но у фантома нет номера' };
        }
        return { ok: true };
    }, [items, notification]);

    // ── Список товаров для левой панели ───────────────────────
    const productsToShow = useMemo(() => {
        let arr = products.filter(p => p.is_active);
        if (categoryFilter === 'none') arr = arr.filter(p => !p.category);
        else if (categoryFilter !== 'all') arr = arr.filter(p => p.category === categoryFilter);
        const q = search.trim().toLowerCase();
        if (q) arr = arr.filter(p => p.name.toLowerCase().includes(q));
        return arr;
    }, [products, categoryFilter, search]);

    const categoryChips = useMemo(() => ([
        { id: 'all',  label: 'Все',           color: 'var(--joy-palette-primary-500)' },
        ...categories.filter(c => c.is_active).map(c => ({
            id: c.id, label: c.name, color: c.color || 'var(--joy-palette-primary-400)',
        })),
        { id: 'none', label: 'Без категории', color: 'var(--joy-palette-text-tertiary)' },
    ]), [categories]);

    const categoryById = useMemo(
        () => Object.fromEntries(categories.map(c => [c.id, c])),
        [categories],
    );

    // ── Пикер скидки ──────────────────────────────────────────
    const openDiscPickerForItem  = (idx) => setDiscPicker({ open: true, target: 'item', itemIdx: idx });
    const openDiscPickerForOrder = ()    => setDiscPicker({ open: true, target: 'order', itemIdx: null });
    const closeDiscPicker        = ()    => setDiscPicker({ open: false, target: 'item', itemIdx: null });
    const handleApplyDiscount    = (disc) => {
        if (discPicker.target === 'order') setOrderDiscount(disc);
        else if (discPicker.itemIdx !== null) setItemDiscount(discPicker.itemIdx, disc);
    };

    // ── Сабмит ────────────────────────────────────────────────
    const handleSubmit = () => {
        if (!validation.ok) return;

        // Новый плоский формат: у каждой позиции свой money_type.
        // Группировка по money_type и пропорциональное распределение
        // скидки на весь чек — на стороне сервера (build_orders_from_flat_items).
        const payload = {
            items: items.map(it => {
                const obj = {
                    product_id: it.product_id,
                    quantity:   it.quantity,
                    unit_price: it.unit_price,
                    money_type: it.money_type,
                    comment:    it.comment || '',
                };
                if (it.discount) {
                    obj.discount = {
                        discount_id: it.discount.discount_id || null,
                        name:        it.discount.name || '',
                        value:       parseFloat(it.discount.value) || 0,
                        is_percent:  !!it.discount.is_percent,
                    };
                }
                return obj;
            }),
            comment: comment.trim() || undefined,
        };
        if (orderDiscount) {
            payload.whole_check_discount = {
                discount_id: orderDiscount.discount_id || null,
                name:        orderDiscount.name || '',
                value:       parseFloat(orderDiscount.value) || 0,
                is_percent:  !!orderDiscount.is_percent,
            };
        }
        if (receiptDt) payload.receipt_datetime = new Date(receiptDt).toISOString();

        onSubmit(payload);
    };

    if (!open || !notification) return null;

    return (
        <>
            <Modal
                open={open}
                onClose={() => !loading && onClose()}
                slotProps={{ backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.65)' } } }}
            >
                <ModalDialog sx={{
                    bgcolor: 'var(--joy-palette-background-level1)',
                    border: '1px solid var(--joy-palette-focusVisible)',
                    borderRadius: 'var(--joy-radius-xl)',
                    width: { xs: 'calc(100vw - 16px)', md: 'min(1180px, calc(100vw - 48px))' },
                    maxWidth: { xs: 'calc(100vw - 16px)', md: 1180 },
                    maxHeight: '95vh', gap: 0,
                    p: 0, overflow: 'hidden',
                    display: 'flex', flexDirection: 'column',
                }}>
                    {/* Шапка */}
                    <Box sx={{
                        px: { xs: 2, sm: 3 }, py: 1.5,
                        borderBottom: '1px solid var(--joy-palette-focusVisible)',
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        bgcolor: 'var(--joy-palette-background-surface)',
                    }}>
                        <Typography sx={{ fontSize: 18, fontWeight: 800 }}>
                            <ReceiptLongRoundedIcon sx={{ fontSize: 20, verticalAlign: 'middle', mr: 0.5, color: '#f59e0b' }} />
                            Оформить чек по фантому
                        </Typography>
                        <ModalClose sx={{ position: 'static' }} />
                    </Box>

                    {/* Тело: две колонки */}
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        flex: 1, minHeight: 0,
                    }}>
                        {/* ─────── ЛЕВАЯ КОЛОНКА: терминал ─────── */}
                        <Box sx={{
                            flex: { md: '1 1 58%' },
                            borderRight:  { md: '1px solid var(--joy-palette-focusVisible)' },
                            borderBottom: { xs: '1px solid var(--joy-palette-divider)', md: 'none' },
                            p: { xs: 1.5, sm: 2 },
                            overflowY: 'auto',
                            maxHeight: { xs: '50vh', md: 'calc(95vh - 60px)' },
                        }}>
                            <Input
                                placeholder="Поиск по товарам…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                size="sm"
                                sx={{ ...inputSx, mb: 1.5 }}
                            />

                            <Typography sx={sectionLabelSx}>
                                <CategoryRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Категории
                            </Typography>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                                {categoryChips.map(c => {
                                    const active = categoryFilter === c.id;
                                    return (
                                        <Box key={c.id}
                                            onClick={() => setCategoryFilter(c.id)}
                                            sx={{
                                                px: 1.2, py: 0.5, fontSize: 12, fontWeight: 700,
                                                borderRadius: 999, cursor: 'pointer',
                                                bgcolor: active ? c.color : 'var(--joy-palette-background-level2)',
                                                color:   active ? '#fff'  : 'var(--joy-palette-text-secondary)',
                                                border: `1.5px solid ${active ? c.color : 'var(--joy-palette-divider)'}`,
                                                transition: 'all .15s',
                                                '&:hover': { borderColor: c.color },
                                            }}>
                                            {c.label}
                                        </Box>
                                    );
                                })}
                            </Box>

                            <Typography sx={sectionLabelSx}>
                                <Inventory2RoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                Товары / услуги
                            </Typography>
                            {productsToShow.length === 0 ? (
                                <Box sx={{ py: 5, textAlign: 'center', color: 'var(--joy-palette-text-tertiary)', fontSize: 13 }}>
                                    Ничего не найдено
                                </Box>
                            ) : (
                                <Box sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', lg: 'repeat(4, 1fr)' },
                                    gap: 1,
                                }}>
                                    {productsToShow.map(p => {
                                        const cat = p.category ? categoryById[p.category] : null;
                                        const catColor = cat?.color || null;
                                        return (
                                            <Box key={p.id}
                                                onClick={() => addItemByProduct(p)}
                                                sx={{
                                                    position: 'relative',
                                                    p: 1, cursor: 'pointer',
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    border: `1px solid ${catColor || 'var(--joy-palette-divider)'}`,
                                                    borderLeftWidth: catColor ? 4 : 1,
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                    transition: 'all .12s',
                                                    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
                                                    minHeight: 64,
                                                    '&:hover': {
                                                        borderColor: 'var(--joy-palette-primary-500)',
                                                        bgcolor: 'rgba(162,38,226,0.08)',
                                                        transform: 'translateY(-1px)',
                                                        boxShadow: '0 4px 12px rgba(162,38,226,0.18)',
                                                    },
                                                    '&:active': { transform: 'scale(0.97)' },
                                                }}>
                                                <Typography sx={{
                                                    fontSize: 12, fontWeight: 700,
                                                    color: 'var(--joy-palette-text-primary)',
                                                    lineHeight: 1.2,
                                                }}>
                                                    {p.name}
                                                </Typography>
                                                <Typography sx={{ fontSize: 13, fontWeight: 800, color: 'var(--joy-palette-primary-300)', mt: 0.4 }}>
                                                    {Number(p.price).toLocaleString('ru-RU')} ₽
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            )}
                        </Box>

                        {/* ─────── ПРАВАЯ КОЛОНКА: инфо о фантоме + параметры ─────── */}
                        <Box sx={{
                            flex: { md: '1 1 42%' },
                            p: { xs: 1.5, sm: 2 },
                            overflowY: 'auto',
                            maxHeight: { xs: 'none', md: 'calc(95vh - 60px)' },
                            display: 'flex', flexDirection: 'column', gap: 1.5,
                        }}>
                            {/* Баннер фантом-чека */}
                            <Box sx={{
                                p: 1.2,
                                bgcolor: 'rgba(245,158,11,0.08)',
                                border: '1.5px solid rgba(245,158,11,0.35)',
                                borderRadius: 'var(--joy-radius-md)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.6 }}>
                                    <NotificationsActiveRoundedIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                                    <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#f59e0b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                        Фантом-чек
                                    </Typography>
                                    <Chip size="sm" sx={{ ml: 'auto', bgcolor: 'rgba(245,158,11,0.18)', color: '#f59e0b', fontSize: 11, fontWeight: 800 }}>
                                        {phantomTotal.toLocaleString('ru-RU')} ₽
                                    </Chip>
                                </Box>

                                <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)' }}>
                                    Фотограф: <b>{notification.photographer_name}</b>
                                </Typography>

                                {/* Разбивка по платежам */}
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6 }}>
                                    {(notification.items || []).map((it, i) => {
                                        const meta = MONEY_META[it.money_type] || MONEY_META.cash;
                                        return (
                                            <Chip key={i} size="sm" startDecorator={meta.icon}
                                                sx={{ bgcolor: `${meta.c}22`, color: meta.c, fontWeight: 700, fontSize: 11 }}>
                                                {parseFloat(it.value).toLocaleString('ru-RU')} ₽ · {meta.label}
                                            </Chip>
                                        );
                                    })}
                                </Box>

                                {/* Привязанный номер */}
                                {notification.payment_number_phone && (
                                    <Box sx={{
                                        mt: 0.8, p: 0.7,
                                        bgcolor: 'rgba(34,197,94,0.10)',
                                        border: '1px solid rgba(34,197,94,0.3)',
                                        borderRadius: 'var(--joy-radius-sm)',
                                        display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap',
                                    }}>
                                        <PhoneIphoneRoundedIcon sx={{ fontSize: 14, color: '#22c55e' }} />
                                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#22c55e' }}>
                                            {notification.payment_number_bank_display} · {notification.payment_number_phone}
                                        </Typography>
                                        {notification.payment_number_owner && (
                                            <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)' }}>
                                                · {notification.payment_number_owner}
                                            </Typography>
                                        )}
                                    </Box>
                                )}

                                {/* Комментарий фотографа */}
                                {notification.comment && (
                                    <Box sx={{ mt: 0.8 }}>
                                        <Typography sx={{ fontSize: 10, fontWeight: 700, color: 'var(--joy-palette-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em', mb: 0.2 }}>
                                            Комментарий фотографа
                                        </Typography>
                                        <Typography sx={{ fontSize: 12, color: 'var(--joy-palette-text-primary)', whiteSpace: 'pre-wrap' }}>
                                            {notification.comment}
                                        </Typography>
                                    </Box>
                                )}
                            </Box>

                            {/* Позиции */}
                            <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.6 }}>
                                    <Typography sx={sectionLabelSx}>
                                        <ShoppingCartRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Позиции
                                    </Typography>
                                    <Chip size="sm" sx={{ bgcolor: 'rgba(162,38,226,0.15)', color: 'var(--joy-palette-primary-300)', fontWeight: 700 }}>
                                        {items.length}
                                    </Chip>
                                </Box>

                                {items.length === 0 ? (
                                    <Box sx={{
                                        p: 2, textAlign: 'center',
                                        border: '1.5px dashed var(--joy-palette-divider)',
                                        borderRadius: 'var(--joy-radius-md)',
                                        color: 'var(--joy-palette-text-tertiary)', fontSize: 12,
                                    }}>
                                        Нажми на товар слева, чтобы добавить позицию
                                    </Box>
                                ) : (
                                    <Stack spacing={0.8}>
                                        {items.map((it, idx) => {
                                            const sub = itemSubtotal(it);
                                            const disc = calcDiscountAmount(sub, it.discount);
                                            const total = Math.max(0, sub - disc);
                                            return (
                                                <Box key={it._uid} sx={{
                                                    p: 1.2,
                                                    border: '1px solid var(--joy-palette-divider)',
                                                    borderRadius: 'var(--joy-radius-md)',
                                                    bgcolor: 'var(--joy-palette-background-level2)',
                                                }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1 }}>
                                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                                            <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'var(--joy-palette-text-primary)' }}>
                                                                {it.product.name}
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.2 }}>
                                                                <Input
                                                                    size="sm" type="number"
                                                                    value={it.unit_price}
                                                                    onChange={(e) => updateItemPrice(idx, e.target.value)}
                                                                    sx={{ ...inputSx, width: 90, fontSize: 12 }}
                                                                    endDecorator={<Typography sx={{ fontSize: 10 }}>₽</Typography>}
                                                                />
                                                                <Typography sx={{ fontSize: 11, color: 'var(--joy-palette-text-tertiary)' }}>
                                                                    × {it.quantity} = <b style={{ color: 'var(--joy-palette-primary-300)' }}>{total.toLocaleString('ru-RU')} ₽</b>
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                        <IconButton size="sm" variant="plain" color="danger"
                                                            onClick={() => removeItem(idx)}>
                                                            <DeleteOutlineRoundedIcon sx={{ fontSize: 18 }} />
                                                        </IconButton>
                                                    </Box>

                                                    {/* Тип оплаты для этой позиции — обязателен */}
                                                    <Box sx={{ mt: 0.6 }}>
                                                        {!it.money_type && (
                                                            <Typography sx={{ fontSize: 10, color: '#ef4444', fontWeight: 700, mb: 0.3 }}>
                                                                Выберите тип оплаты
                                                            </Typography>
                                                        )}
                                                        <Stack direction="row" spacing={0.3}>
                                                            {MONEY_TYPES.map(t => {
                                                                const active = it.money_type === t.v;
                                                                const needsAttention = !it.money_type;
                                                                return (
                                                                    <Box key={t.v}
                                                                        onClick={(e) => { e.stopPropagation(); setItemMoneyType(idx, t.v); }}
                                                                        sx={{
                                                                            flex: 1, py: 0.4,
                                                                            cursor: 'pointer',
                                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.3,
                                                                            borderRadius: 'var(--joy-radius-sm)',
                                                                            bgcolor: active ? t.c : 'transparent',
                                                                            color:   active ? '#fff' : t.c,
                                                                            border: `1px solid ${active ? t.c : (needsAttention ? '#ef4444' : `${t.c}40`)}`,
                                                                            fontSize: 10, fontWeight: 700,
                                                                            transition: 'all .12s',
                                                                            '&:hover': { borderColor: t.c, bgcolor: active ? t.c : `${t.c}18` },
                                                                            '& .MuiSvgIcon-root': { fontSize: 12, color: active ? '#fff' : t.c },
                                                                        }}>
                                                                        {t.icon}
                                                                        <Box component="span">{t.label}</Box>
                                                                    </Box>
                                                                );
                                                            })}
                                                        </Stack>
                                                    </Box>

                                                    {it.discount ? (
                                                        <Box sx={{
                                                            mt: 0.8, p: 0.8,
                                                            bgcolor: 'rgba(245,158,11,0.10)',
                                                            border: '1px solid rgba(245,158,11,0.3)',
                                                            borderRadius: 'var(--joy-radius-sm)',
                                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                                <LocalOfferRoundedIcon sx={{ fontSize: 13, color: '#f59e0b' }} />
                                                                <Typography sx={{ fontSize: 11, color: '#f59e0b', fontWeight: 700 }}>
                                                                    {it.discount.name || 'Скидка'} · −{it.discount.value}{it.discount.is_percent ? '%' : '₽'}
                                                                </Typography>
                                                            </Box>
                                                            <IconButton size="sm" variant="plain" color="danger"
                                                                onClick={() => setItemDiscount(idx, null)}>
                                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 14 }} />
                                                            </IconButton>
                                                        </Box>
                                                    ) : (
                                                        <Button
                                                            size="sm" variant="plain"
                                                            startDecorator={<LocalOfferRoundedIcon sx={{ fontSize: 14 }} />}
                                                            onClick={() => openDiscPickerForItem(idx)}
                                                            sx={{ mt: 0.5, fontSize: 11, color: '#f59e0b', minHeight: 24 }}>
                                                            Скидка
                                                        </Button>
                                                    )}

                                                    <Box sx={{
                                                        mt: 0.8,
                                                        display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5,
                                                    }}>
                                                        <IconButton size="sm" variant="outlined" color="neutral"
                                                            onClick={() => updateItemQty(idx, -1)}
                                                            disabled={it.quantity <= 1}
                                                            sx={{ width: 28, height: 28, borderRadius: 'var(--joy-radius-sm)' }}>
                                                            <RemoveRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                        <Typography sx={{
                                                            minWidth: 28, textAlign: 'center',
                                                            fontSize: 14, fontWeight: 800,
                                                            color: 'var(--joy-palette-text-primary)',
                                                        }}>
                                                            {it.quantity}
                                                        </Typography>
                                                        <IconButton size="sm" variant="outlined" color="primary"
                                                            onClick={() => updateItemQty(idx, 1)}
                                                            sx={{ width: 28, height: 28, borderRadius: 'var(--joy-radius-sm)' }}>
                                                            <AddRoundedIcon sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    </Box>
                                                </Box>
                                            );
                                        })}
                                    </Stack>
                                )}
                            </Box>

                            {/* Скидка на чек */}
                            {items.length > 0 && (
                                <Box>
                                    <Typography sx={sectionLabelSx}>
                                        <LocalOfferRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                        Скидка на весь чек
                                    </Typography>
                                    {orderDiscount ? (
                                        <Box sx={{
                                            p: 1,
                                            bgcolor: 'rgba(245,158,11,0.10)',
                                            border: '1px solid rgba(245,158,11,0.3)',
                                            borderRadius: 'var(--joy-radius-md)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        }}>
                                            <Typography sx={{ fontSize: 12, color: '#f59e0b', fontWeight: 700 }}>
                                                {orderDiscount.name} · −{orderDiscount.value}{orderDiscount.is_percent ? '%' : '₽'}
                                                <Box component="span" sx={{ ml: 0.5, color: 'var(--joy-palette-text-tertiary)' }}>
                                                    (−{orderDiscAmount.toLocaleString('ru-RU')} ₽)
                                                </Box>
                                            </Typography>
                                            <IconButton size="sm" variant="plain" color="danger"
                                                onClick={() => setOrderDiscount(null)}>
                                                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
                                            </IconButton>
                                        </Box>
                                    ) : (
                                        <Button
                                            size="sm" variant="outlined"
                                            startDecorator={<AddRoundedIcon sx={{ fontSize: 14 }} />}
                                            onClick={openDiscPickerForOrder}
                                            sx={{ color: '#f59e0b', borderColor: 'rgba(245,158,11,0.3)' }}>
                                            Добавить скидку на чек
                                        </Button>
                                    )}
                                </Box>
                            )}

                            {/* Комментарий админа */}
                            <Box>
                                <Typography sx={sectionLabelSx}>Комментарий админа</Typography>
                                <Textarea
                                    size="sm" minRows={2} maxRows={3}
                                    placeholder="Доп. инфа от админа (опц.)"
                                    value={comment}
                                    onChange={(e) => setComment(e.target.value.slice(0, 200))}
                                    sx={{ ...inputSx, color: 'white' }}
                                />
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                    Комментарий фотографа автоматически добавится в чек.
                                </Typography>
                            </Box>

                            {/* Дата и время */}
                            <Box>
                                <Typography sx={sectionLabelSx}>
                                    <ScheduleRoundedIcon sx={{ fontSize: 12, verticalAlign: 'middle', mr: 0.3 }} />
                                    Дата и время чека
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                    <Input
                                        size="sm" type="datetime-local"
                                        value={receiptDt}
                                        onChange={(e) => setReceiptDt(e.target.value)}
                                        placeholder={nowLocal()}
                                        sx={{ ...inputSx, flex: 1, color: 'white' }}
                                    />
                                    {receiptDt && (
                                        <Button size="sm" variant="soft" color="neutral"
                                            onClick={() => setReceiptDt(
                                                isoToDatetimeLocal(notification?.date_create) || ''
                                            )}>
                                            Как у фотографа
                                        </Button>
                                    )}
                                </Box>
                                <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', mt: 0.3 }}>
                                    {receiptDt
                                        ? `Будет оформлен на ${new Date(receiptDt).toLocaleString('ru-RU')}`
                                        : notification?.date_create
                                            ? `По умолчанию — момент добавления фотографом (${new Date(notification.date_create).toLocaleString('ru-RU')}). Меняйте только для бэкдейта.`
                                            : 'По умолчанию — момент добавления фотографом. Меняйте только для бэкдейта.'}
                                </Typography>
                            </Box>

                            {/* Итог */}
                            <Box sx={{
                                mt: 'auto',
                                p: 1.5,
                                bgcolor: 'rgba(34,197,94,0.10)',
                                border: '1px solid rgba(34,197,94,0.35)',
                                borderRadius: 'var(--joy-radius-md)',
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Итого:</Typography>
                                    <Typography sx={{ fontSize: 22, fontWeight: 900, color: '#22c55e' }}>
                                        {grandTotal.toLocaleString('ru-RU')} ₽
                                    </Typography>
                                </Box>
                                {orderDiscAmount > 0 && (
                                    <Typography sx={{ fontSize: 10, color: 'var(--joy-palette-text-tertiary)', textAlign: 'right' }}>
                                        включая скидку на чек −{orderDiscAmount.toLocaleString('ru-RU')} ₽
                                    </Typography>
                                )}
                                {phantomTotal > 0 && Math.abs(grandTotal - phantomTotal) >= 1 && (
                                    <Typography sx={{ fontSize: 10, color: '#f59e0b', textAlign: 'right', mt: 0.2 }}>
                                        {grandTotal > phantomTotal ? '+' : '−'}
                                        {Math.abs(grandTotal - phantomTotal).toLocaleString('ru-RU')} ₽ относительно фантома ({phantomTotal.toLocaleString('ru-RU')} ₽)
                                    </Typography>
                                )}
                                {/* Разбивка по типам оплаты */}
                                {moneyTypesBreakdown.length > 1 && (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, mt: 0.6, justifyContent: 'flex-end' }}>
                                        {moneyTypesBreakdown.map(({ mt, total }) => {
                                            const meta = MONEY_META[mt];
                                            if (!meta) return null;
                                            return (
                                                <Chip key={mt} size="sm" startDecorator={meta.icon}
                                                    sx={{ bgcolor: `${meta.c}20`, color: meta.c, fontSize: 10, fontWeight: 700 }}>
                                                    {total.toLocaleString('ru-RU')} ₽
                                                </Chip>
                                            );
                                        })}
                                    </Box>
                                )}
                            </Box>

                            {/* Кнопки */}
                            <Stack direction="row" spacing={1}>
                                <Button variant="soft" color="danger" onClick={onClose}
                                    sx={{ flex: 0 }}>
                                    Отмена
                                </Button>
                                <Button
                                    fullWidth
                                    loading={loading}
                                    disabled={!validation.ok}
                                    onClick={handleSubmit}
                                    startDecorator={<ReceiptLongRoundedIcon />}
                                    sx={{
                                        flex: 1,
                                        background: validation.ok
                                            ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
                                            : 'var(--joy-palette-background-level2)',
                                        color: '#fff', fontWeight: 800,
                                        opacity: validation.ok ? 1 : 0.5,
                                    }}>
                                    {validation.ok
                                        ? `Оформить чек на ${grandTotal.toLocaleString('ru-RU')} ₽`
                                        : (validation.reason || 'Заполните форму')}
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                </ModalDialog>
            </Modal>

            <DiscountPickerModal
                open={discPicker.open}
                onClose={closeDiscPicker}
                onApply={handleApplyDiscount}
                discounts={discounts}
                categories={categories}
                product={
                    discPicker.target === 'item' && discPicker.itemIdx !== null
                        ? items[discPicker.itemIdx]?.product || null
                        : null
                }
                target={discPicker.target}
            />
        </>
    );
}